import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { styleMap } from 'lit/directives/style-map.js';
import { consumeContext } from '../../base/context';
import { ElementLogger } from '../../base/logger';
import { DEV_MODE } from '../../global/env';
import { mediaContext } from '../../media';
import { formatSpokenTime } from '../../utils/time';
import { seekableProgressBarElementStyles } from './styles';
export const SEEKABLE_PROGRESS_BAR_ELEMENT_TAG_NAME = 'vds-seekable-progress-bar';
/**
 * Displays a progress bar from 0 to media duration with the amount of media that is seekable.
 * Seekable media is parts of the media that been downloaded and seeking to it won't result
 * in any delay or any additional loading.
 *
 * @tagname vds-seekable-progress-bar
 * @slot Used to pass content into the progress element.
 * @csspart root - The progress bar element (`<div>`).
 * @cssprop --vds-seekable-progress-bar-bg - The background color of the amount that is seekable (defaults to `#616161`).
 * @cssprop --vds-seekable-progress-bar-height - The height of the progress bar (defaults to `--vds-slider-track-height`).
 * @example
 * ```html
 * <vds-seekable-progress-bar
 *   label="Amount of seekable media"
 * ></vds-seekable-progress-bar>
 * ```
 */
export class SeekableProgressBarElement extends LitElement {
    constructor() {
        super(...arguments);
        // -------------------------------------------------------------------------------------------
        // Properties
        // -------------------------------------------------------------------------------------------
        /* c8 ignore next */
        this._logger = DEV_MODE && new ElementLogger(this);
        /**
         * ♿ **ARIA:** The `aria-label` for the progress bar.
         */
        this.label = 'Amount of seekable media';
        /**
         * ♿ **ARIA:** Human-readable text alternative for the seekable amount. If you pass
         * in a string containing `{seekableAmount}` or `{duration}` templates they'll be replaced with
         * the spoken form such as `1 hour 30 minutes`.
         */
        this.valueText = '{seekableAmount} out of {duration}';
        this._mediaSeekableAmount = mediaContext.seekableAmount.initialValue;
        this._mediaDuration = 0;
        // -------------------------------------------------------------------------------------------
        // Progress
        // -------------------------------------------------------------------------------------------
        this._progressBarRef = createRef();
    }
    static get styles() {
        return [seekableProgressBarElementStyles];
    }
    static get parts() {
        return ['root'];
    }
    // -------------------------------------------------------------------------------------------
    // Lifecycle
    // -------------------------------------------------------------------------------------------
    render() {
        return this._renderProgressBar();
    }
    /**
     * Returns the underlying `<progress>` element.
     */
    get progressBarElement() {
        return this._progressBarRef.value;
    }
    _renderProgressBar() {
        return html `
      <div
        id="progressbar"
        role="progressbar"
        part="root"
        style=${styleMap({
            '--vds-media-seekable': String(this._mediaSeekableAmount),
            '--vds-media-duration': String(this._mediaDuration)
        })}
        ?hidden=${this.hidden}
        aria-label=${this.label}
        aria-valuemin="0"
        aria-valuenow=${Math.round(this._mediaSeekableAmount)}
        aria-valuemax=${Math.round(this._mediaDuration)}
        aria-valuetext=${this._getProgressBarValueText()}
        ${ref(this._progressBarRef)}
      >
        ${this._renderProgressBarChildren()}
      </div>
    `;
    }
    _getProgressBarValueText() {
        return this.valueText
            .replace('{seekableAmount}', formatSpokenTime(this._mediaSeekableAmount))
            .replace('{duration}', formatSpokenTime(this._mediaDuration));
    }
    _renderProgressBarChildren() {
        return this._renderProgressBarSlot();
    }
    _renderProgressBarSlot() {
        return html `<slot></slot>`;
    }
}
__decorate([
    property()
], SeekableProgressBarElement.prototype, "label", void 0);
__decorate([
    property({ attribute: 'value-text' })
], SeekableProgressBarElement.prototype, "valueText", void 0);
__decorate([
    state(),
    consumeContext(mediaContext.seekableAmount)
], SeekableProgressBarElement.prototype, "_mediaSeekableAmount", void 0);
__decorate([
    state(),
    consumeContext(mediaContext.duration, { transform: (d) => (d >= 0 ? d : 0) })
], SeekableProgressBarElement.prototype, "_mediaDuration", void 0);
